import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import type { Review } from "@/services/api/odin/activities/diagnostic/study-session/update"
import { Loader2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { motion } from "framer-motion"

interface AnalysisQuestionProps {
  review: Review
  displayReview: boolean
  isLoading: boolean
  onClickSendAnswer: (answer: string, correctAnswer: number) => void
  onClickNextQuestion: () => void
}

export default function AnalysisQuestion({
  review,
  displayReview,
  isLoading,
  onClickSendAnswer,
  onClickNextQuestion,
}: AnalysisQuestionProps) {
  const [value, setValue] = useState("")
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    setValue("")
    setIsError(false)
  }, [])

  const handleSubmitAnswer = () => {
    if (displayReview) {
      setValue("")
      onClickNextQuestion()
      return
    }

    const trimmedValue = value.trim()
    if (!trimmedValue) {
      setIsError(true)
      return
    }

    setIsError(false)
    onClickSendAnswer(trimmedValue, 0)
  }

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  }

  return (
    <div className="flex flex-col gap-4">
      <textarea
        disabled={displayReview}
        className={`min-h-28 w-full rounded-md border p-3 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
          isError
            ? "border-red-500 focus:border-red-500"
            : "border-neutral-300 focus:border-primary-500"
        }`}
        placeholder="Escribe tu respuesta aquí..."
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          if (e.target.value.trim()) setIsError(false)
        }}
      />
      {isError && <span className="text-sm text-red-600">Debes ingresar una respuesta.</span>}
      <div className="flex justify-end">
        <Button onClick={handleSubmitAnswer} disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : displayReview ? (
            "Continuar"
          ) : (
            "Enviar"
          )}
        </Button>
      </div>
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate={displayReview ? "visible" : "hidden"}
        exit="exit"
        className="pt-3"
      >
        <Card className="w-full rounded-md">
          <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex justify-end">
              <span className="rounded bg-neutral-200 px-2 py-1 text-sm font-medium">
                {review.score}/10
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-lg font-semibold">Respuesta</p>
              <p className="text-sm text-neutral-700">{review.review}</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
